/**
 * New permit filing — envelope review (capability tiers, companion §2.8).
 *
 * Choose jurisdiction + permit type for a job, preview the auto-selected capability tier,
 * then build the canonical envelope + draft application (createEnvelope). On success, route to
 * the new application's packet. Prepare-to-attest: nothing is submitted here.
 *
 * Wiring (mobile inv. 7): useQuery(listJurisdictions/listPermitTypes/getFilingTier) +
 * useMutation(createEnvelope). Loading/empty/error render. NEVER imports @smartbox/*.
 */

import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listJurisdictions,
  listPermitTypes,
  getFilingTier,
  createEnvelope,
  type NewFilingRequest,
} from '../../../src/api/permits';
import { Screen, Text, Card, Button, Field, Select } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

export default function NewFilingScreen() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams<{ jobId?: string }>();

  const [jobId, setJobId] = useState(params.jobId ?? '');
  const [jurisdictionId, setJurisdictionId] = useState<string | null>(null);
  const [permitTypeId, setPermitTypeId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const jurisdictionsQuery = useQuery({ queryKey: ['jurisdictions'], queryFn: listJurisdictions, staleTime: 60_000 });
  const typesQuery = useQuery({ queryKey: ['permit-types'], queryFn: listPermitTypes, staleTime: 60_000 });
  const tierQuery = useQuery({
    queryKey: ['filing-tier', jurisdictionId],
    queryFn: () => getFilingTier(jurisdictionId!),
    enabled: !!jurisdictionId,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: NewFilingRequest) => createEnvelope(body),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['permit-applications'] });
      router.replace({ pathname: '/permits/packet', params: { appId: res.application.id } });
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not start the filing.')),
  });

  const onCreate = () => {
    setFormError(null);
    if (!jobId.trim()) return setFormError('A job ID is required to build the envelope.');
    if (!jurisdictionId) return setFormError('Choose a jurisdiction.');
    if (!permitTypeId) return setFormError('Choose a permit type.');
    createMutation.mutate({ jobId: jobId.trim(), jurisdictionId, permitTypeId });
  };

  const jurisdictions = jurisdictionsQuery.data ?? [];
  const types = typesQuery.data ?? [];

  if (jurisdictionsQuery.isLoading || typesQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState} testID="permit-new-filing-screen">
          <ActivityIndicator size="large" color={theme.color.action.primary} accessibilityLabel="Loading" />
          <Text variant="body" color="secondary">Loading…</Text>
        </View>
      </Screen>
    );
  }

  return (
    <Screen scroll>
      <View testID="permit-new-filing-screen" style={styles.section}>
        <Text variant="title">New filing</Text>
        <Card>
          <Text variant="small" color="secondary">
            Builds the canonical permit envelope from the job + a draft application. Nothing is
            submitted yet; you review and file per the jurisdiction's method.
          </Text>
        </Card>

        <Field
          label="Job ID"
          placeholder="Job to file for"
          value={jobId}
          onChangeText={setJobId}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Job ID"
        />

        <View style={styles.section}>
          <Text variant="label" color="secondary">Jurisdiction</Text>
          {jurisdictions.length === 0 ? (
            <Card><Text variant="body" color="secondary">No jurisdictions yet. Add one under Manage.</Text></Card>
          ) : (
            <Select options={jurisdictions.map((j) => ({ value: j.id, label: j.name }))} value={jurisdictionId} onChange={setJurisdictionId} />
          )}
        </View>

        <View style={styles.section}>
          <Text variant="label" color="secondary">Permit type</Text>
          {types.length === 0 ? (
            <Card><Text variant="body" color="secondary">No permit types yet.</Text></Card>
          ) : (
            <Select options={types.map((t) => ({ value: t.id, label: t.code ? `${t.name} (${t.code})` : t.name }))} value={permitTypeId} onChange={setPermitTypeId} />
          )}
        </View>

        {jurisdictionId ? (
          <Card testID="filing-tier-preview">
            <Text variant="small" color="secondary">
              {tierQuery.isLoading
                ? 'Resolving filing tier…'
                : tierQuery.isError
                  ? 'Could not resolve the tier (Tier 0 packet still available).'
                  : `Auto-selected tier: ${tierQuery.data?.capability_tier} (${humanize(tierQuery.data?.filing_method ?? 'packet')}).`}
            </Text>
          </Card>
        ) : null}

        {formError ? (
          <Card><Text variant="bodyStrong" color="danger" accessibilityRole="alert">{formError}</Text></Card>
        ) : null}

        <Button
          title="Build envelope & start filing"
          testID="permit-new-filing-submit"
          onPress={onCreate}
          loading={createMutation.isPending}
          disabled={createMutation.isPending || !jobId.trim() || !jurisdictionId || !permitTypeId}
          accessibilityLabel="Build the permit envelope and create a draft application"
        />
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: { flex: 1, justifyContent: 'center', alignItems: 'center', gap: theme.spacing.md, paddingHorizontal: theme.spacing['2xl'] },
  section: { gap: theme.spacing.sm },
});
